import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { ShareService } from '../services/shareService';
import { pathWithLang } from '../utils/navigation';
import { useTranslation } from 'react-i18next';
import { getVerdictI18nKey } from '../utils/verdictCalculator';
import CategoryBadge from './CategoryBadge';
import UserAvatar from './UserAvatar';

interface PublicShare {
  id: number;
  share_id: string;
  title: string;
  description: string;
  category: string;
  verdict: string;
  share_created_at: string;
  share_url?: string;
  external_id?: string;
}

interface PublicSharesPageProps {
  onBack: () => void;
  theme: 'dark' | 'light';
  language: 'ru' | 'en';
}

const PublicSharesPage: React.FC<PublicSharesPageProps> = ({ onBack, theme, language }) => {
  const { t } = useTranslation('publicShares');
  const { t: tAnalysis } = useTranslation('analysis');
  const logoRef = useRef<HTMLButtonElement>(null);
  const [shares, setShares] = useState<PublicShare[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(() => {
    // Читаем параметр page из URL при инициализации
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const page = parseInt(urlParams.get('page') || '1', 10);
      return page > 0 ? page : 1;
    }
    return 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const loadingPagesRef = useRef<Map<number, boolean>>(new Map()); // Отслеживаем загружающиеся страницы
  const currentPageRef = useRef(currentPage); // Отслеживаем актуальную страницу

  const itemsPerPage = 12; // Ограничено для теста

  // При смене языка сбрасываем на первую страницу
  useEffect(() => {
    setCurrentPage(1);
  }, [language]);

  // Функция для получения URL страницы
  const getPageUrl = (page: number): string => {
    if (page === 1) {
      return '/public-shares';
    }
    return `/public-shares?page=${page}`;
  };

  // Цвета для вердиктов (категории теперь используют CategoryBadge)

  // Цвета для вердиктов
  const verdictColors = {
    'ДА': 'bg-green-100 text-green-700 border-green-200',
    'НЕТ': 'bg-red-100 text-red-700 border-red-200'
  };

  // Загрузка данных
  useEffect(() => {
    // Обновляем ref актуальной страницы
    currentPageRef.current = currentPage;
    
    // Проверяем, не загружается ли уже эта страница
    if (loadingPagesRef.current.get(currentPage)) {
      return;
    }
    
    // СИНХРОННО помечаем страницу как загружающуюся
    loadingPagesRef.current.set(currentPage, true);
    
    const pageToLoad = currentPage; // Сохраняем страницу для проверки
    
    const loadShares = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const offset = (pageToLoad - 1) * itemsPerPage;
        const response = await ShareService.getPublicShares(itemsPerPage, offset, language);
        
        // Проверяем, не изменилась ли страница через ref (актуальное значение)
        if (pageToLoad !== currentPageRef.current) {
          return;
        }
        
        setShares(response.data || []);
        setHasMore(response.pagination.has_more);
        
        // Вычисляем общее количество страниц на основе has_more
        if (response.pagination.has_more) {
          setTotalPages(pageToLoad + 1); // Показываем текущую + 1, если есть еще данные
        } else {
          setTotalPages(pageToLoad);
        }
      } catch (err) {
        // Игнорируем ошибки, если страница изменилась
        if (pageToLoad !== currentPageRef.current) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Ошибка загрузки данных');
        setShares([]);
      } finally {
        // Обновляем состояние только если страница не изменилась
        if (pageToLoad === currentPageRef.current) {
          setIsLoading(false);
        }
        // Удаляем страницу из Map только после завершения запроса
        loadingPagesRef.current.delete(pageToLoad);
      }
    };

    loadShares();
    
    // Cleanup: не делаем ничего, так как в StrictMode cleanup вызывается между вызовами
    // но это не значит, что страница изменилась - флаг будет удален в finally
    return () => {
      // Пустой cleanup - не мешаем запросу завершиться
    };
  }, [currentPage, language]);

  // Обработка навигации браузера (назад/вперед)
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const page = parseInt(urlParams.get('page') || '1', 10);
      const newPage = page > 0 ? page : 1;
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentPage]);


  // Обработчики
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Обновляем URL без перезагрузки страницы
    const newUrl = pathWithLang(getPageUrl(page));
    window.history.pushState({}, '', newUrl);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShareClick = (share: PublicShare) => {
    const path = share.share_url || `/share/${share.share_id}`;
    window.open(path, '_blank');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = language === 'ru' ? 'ru-RU' : 'en-US';
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={`text-lg ${theme === 'dark' ? 'text-white' : 'text-gray-700'}`}>
            Загружаем публичные анализы...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">{t('errorTitle')}</h2>
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-900' : 'bg-gradient-to-br from-white to-gray-50'}`}>
      {/* Заголовок */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={t('back')}
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              {/* Логотип как ссылка на главную */}
              <div className="flex items-center flex-shrink-0 relative">
                <img 
                  src="/images/Plinus_Valonik_Logo_Vector_2025.svg"
                  alt={t('logoAlt')} 
                  className="h-10 sm:h-12 object-contain transition-all duration-300 pointer-events-none"
                />
                <button
                  ref={logoRef}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onBack && typeof onBack === 'function') {
                      onBack();
                    }
                  }}
                  className="absolute inset-0 w-full h-full bg-transparent border-0 p-0 cursor-pointer hover:opacity-80 transition-opacity z-10"
                  style={{ zIndex: 10, pointerEvents: 'auto' }}
                  title={t('toHome')}
                  aria-label={t('toHome')}
                />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">
                {t('pageTitle')}
              </h1>
            </div>
          </div>
        </div>
      </div>


      {/* Контент */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('pageTitle')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('pageDescription')}
          </p>
        </div>
        {shares.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Calendar className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('noResultsTitle')}
            </h3>
            <p className="text-gray-600">
              {t('noResultsDescription')}
            </p>
          </div>
        ) : (
          <>
            {/* Сетка анализов */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
              {shares.map((share, index) => {
                // Определяем цвет фона карточки в зависимости от вердикта
                const cardBackgroundColor = share.verdict === "ДА" 
                  ? "bg-green-50 border-green-200 hover:border-green-300" 
                  : "bg-red-50 border-red-200 hover:border-red-300";
                
                return (
                  <a 
                    key={share.id}
                    href={share.share_url || `/share/${share.share_id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleShareClick(share);
                    }}
                    className={`group ${cardBackgroundColor} rounded-lg border hover:shadow-md transition-all duration-200 p-4 h-32 flex flex-col justify-between`}
                    style={{
                      animationDelay: `${index * 100}ms`, // Задержка анимации для эффекта водопада
                    }}
                  >
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <UserAvatar size="sm" />
                        <CategoryBadge 
                          category={share.category} 
                          size="sm" 
                        />
                        <span className="text-xs text-gray-500">{formatDate(share.share_created_at)}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {share.title}
                      </h3>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600 line-clamp-1 flex-1 mr-3">
                        {share.description}
                      </p>
                      <div className="flex items-center space-x-2">
                        <div className={`flex items-center px-2 py-1 rounded text-xs font-semibold ${
                          verdictColors[share.verdict as keyof typeof verdictColors] || 'bg-gray-100 text-gray-700'
                        }`}>
                          {(() => { const k = getVerdictI18nKey(share.verdict); return k ? tAnalysis(k) : share.verdict; })()}
                        </div>
                        <div className="flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-full group-hover:bg-blue-200 group-hover:scale-110 transition-all duration-200">
                          <ArrowRight className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>

            {/* Пагинация */}
            <nav className="flex items-center justify-center space-x-2" aria-label={t('paginationAria')}>
              {currentPage > 1 ? (
                <a
                  href={getPageUrl(currentPage - 1)}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(currentPage - 1);
                  }}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  aria-label={t('prevPageAria')}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {t('prevPage')}
                </a>
              ) : (
                <span className="flex items-center px-3 py-2 text-sm font-medium text-gray-300 bg-white border border-gray-200 rounded-lg opacity-50 cursor-not-allowed">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {t('prevPage')}
                </span>
              )}

              <span className="px-4 py-2 text-sm text-gray-700">
                {t('pageOf', { page: currentPage })}
              </span>

              {hasMore ? (
                <a
                  href={getPageUrl(currentPage + 1)}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePageChange(currentPage + 1);
                  }}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  aria-label={t('nextPageAria')}
                >
                  {t('nextPage')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </a>
              ) : (
                <span className="flex items-center px-3 py-2 text-sm font-medium text-gray-300 bg-white border border-gray-200 rounded-lg opacity-50 cursor-not-allowed">
                  {t('nextPage')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </span>
              )}
            </nav>
          </>
        )}
      </div>
    </div>
  );
};

export default PublicSharesPage;
